/**
 * @fileoverview Component for displaying a list of sites.
 * This component includes a search bar, filters, and pagination.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetSitesCountQuery, useGetSitesQuery } from '../../services/sites'
import { Search } from '../elements/Search'
import { Pagination } from '../Pagination'
import { SiteEmptyList } from './SiteEmptyList'
import { SiteListTable } from './SiteListTable'

/**
 * @function SiteList
 * @description Component for displaying a list of sites.
 * @returns {JSX.Element} - A React component that renders the list of sites.
 */
export function SiteList() {
    const navigate = useNavigate()

    const { data: sites, isLoading, refetch: refetchSites } = useGetSitesQuery()
    const { data: total, refetch: refetchSitesCount } = useGetSitesCountQuery()

    useEffect(() => {
        refetchSites()
        refetchSitesCount()
    }, [location.search])

    function handleRowClick(e: Event, userId: string) {
        if (e.target instanceof HTMLInputElement) {
            return
        }
        navigate(userId + location.search)
    }

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex flex-wrap fixed top-0 left-[256px] right-0 p-8 bg-white z-20 border-b justify-between">
                <div className="flex flex-col gap-y-6">
                    <div className="flex items-center h-full">
                        <span className="text-2xl font-semibold mr-3">Sites</span>
                    </div>
                    <Search placeholder="Search by site name..." />
                </div>
            </div>
            <div className="relative w-full mt-[161px]">
                <div className="flex flex-wrap bg-white">
                    <div className="w-full min-h-[calc(100vh_-_204px)] flex flex-wrap">
                        <div className="inline-block min-w-full align-middle">
                            {total?.count > 0 ? (
                                <div className="flex flex-col">
                                    <SiteListTable data={sites} onRowClicked={handleRowClick} />
                                    <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                                        <Pagination total={total?.count} />
                                    </div>
                                </div>
                            ) : (
                                <SiteEmptyList />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
